import { Box, Dialog, DialogContent, Theme } from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridToolbarContainer,
  GridToolbarContainerProps,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useState } from 'react';

import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import useApplication from '@/hooks/useApplication';
import { ApplicationDataGridDetailsProps } from '@/models/Application';
import { ApplicationChangeLogEntry, ChangeLogType } from '@/models/ApplicationChangeLogEntry';
import { addEvenOddRowClassName, GridLocaleTextOverride } from '@/utils/dataGridUtil';
import { LocaleContext } from '@/Contexts/LocaleContext';

export default function ApplicationChangeLogModal({
  showModal,
  setShowModal,
  modalApplication,
  theme,
}: {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  modalApplication: ApplicationDataGridDetailsProps;
  theme: Theme;
}) {
  const { locale } = useContext(LocaleContext);
  const { getApplicationChangeLog } = useApplication();

  const [questionChangeLogEntries, setQuestionChangeLogEntries] = useState<
    ApplicationChangeLogEntry[] | null
  >(null);

  const userTimeZone = dayjs.tz.guess();

  useEffect(() => {
    if (!questionChangeLogEntries) {
      getApplicationChangeLog(modalApplication.applicationId).then(
        (changeLogEntries: ApplicationChangeLogEntry[] | null) => {
          setQuestionChangeLogEntries(changeLogEntries);
        }
      );
    }
  }, [getApplicationChangeLog, modalApplication.applicationId, questionChangeLogEntries]);

  const handleModalClose = () => {
    setShowModal(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'question',
      headerName: 'Question',
      minWidth: 40, // Adjusted from flex
      flex: 2,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'data-grid-header-green',
    },
    {
      field: 'standardName',
      headerName: 'Standard',
      minWidth: 40, // Adjusted from flex
      flex: 2,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'data-grid-header-green',
    },
    {
      field: 'changeLogDateTimeUtc',
      headerName: 'Changed At',
      align: 'center',
      headerAlign: 'center',
      minWidth: 180, // Adjusted from flex
      flex: 0.5,
      valueGetter: (value: string) => {
        if (value == null || !value) return '';

        const userTimeZoneCreatedOn = dayjs.utc(value).tz(userTimeZone);

        if (userTimeZoneCreatedOn) {
          return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          }).format(userTimeZoneCreatedOn.toDate());
        }

        return '';
      },
    },
    {
      field: 'userEmail',
      headerName: 'Changed By',
      minWidth: 180, // Adjusted from flex
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'applicationStatus',
      headerName: 'Application Status',
      align: 'center',
      headerAlign: 'center',
      minWidth: 180, // Adjusted from flex
      flex: 0.5,
    },
    {
      field: 'oldResponse',
      headerName: 'Old Response',
      headerAlign: 'left',
      align: 'left',
      minWidth: 75, // Adjusted from flex
      flex: 2,
    },
    {
      field: 'newResponse',
      headerAlign: 'left',
      align: 'left',
      headerName: 'New Response',
      minWidth: 150, // Adjusted from flex
      flex: 2,
    },
  ];

  const GridToolBarCustom = useCallback((props: GridToolbarContainerProps) => {
    return (
      <GridToolbarContainer {...props}>
        <GridToolbarFilterButton />
        <GridToolbarExport />
        <div style={{ flex: 1 }}></div>
        <GridToolbarQuickFilter debounceMs={1500} />
      </GridToolbarContainer>
    );
  }, []);

  const datagridSx = {
    '& .application-status': {
      '&, &:hover, &.Mui-selected, &.Mui-selected:hover': {
        backgroundColor: theme.palette.warningBackground.main,
      },
    },
  };

  return (
    <Dialog open={showModal} onClose={handleModalClose} maxWidth={false}>
      <DialogTitleWithClose
        onClose={handleModalClose}
      >{`Application Change Log for AppID: ${modalApplication.applicationId}`}</DialogTitleWithClose>
      <DialogContent>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '80vw',
            height: '60vh',
          }}
        >
          <DataGridPremium
            showToolbar
            localeText={GridLocaleTextOverride(locale)}
            getRowId={(row) => row.question + row.changeLogDateTimeUtc}
            getRowClassName={addEvenOddRowClassName((params) =>
              params.row.changeType === ChangeLogType.QuestionResponse
                ? 'question-response'
                : 'application-status'
            )}
            sx={datagridSx}
            initialState={{ density: 'compact' }}
            pagination
            autoPageSize
            rows={questionChangeLogEntries ?? []}
            columns={columns}
            disableColumnResize
            slots={{
              toolbar: GridToolBarCustom,
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
